import { Check, Star, Zap, Headphones, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

export const PLANS = [
  {
    id: "hogar-300",
    tier: "HOGAR",
    speed: "300 Mbps",
    price: "$29.900",
    priceSuffix: "/mes",
    description: "Fibra óptica ideal para el día a día y toda la familia.",
    features: [
      "Fibra óptica FTTH directa",
      "WiFi Router dual-band incluido",
      "Sin límite de datos",
      "Soporte local prioritario",
    ],
    cta: "Contratar",
    accent: "sky",
  },
  {
    id: "hogar-500",
    tier: "HOGAR PRO",
    speed: "500 Mbps",
    price: "$39.900",
    priceSuffix: "/mes",
    description:
      "Velocidad top para gaming, streaming 4K y familias grandes.",
    features: [
      "Fibra óptica FTTH directa",
      "WiFi 6 Router incluido",
      "Sin límite de datos",
      "Soporte local 24/7",
      "Prioridad en la instalación",
    ],
    cta: "Contratar",
    accent: "pink",
    recommended: true,
  },
];

const ACCENTS = {
  sky: {
    priceText: "text-sky-600",
    cta: "bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white",
    check: "text-sky-500",
    border: "border-slate-100 hover:border-sky-300",
    shadow: "hover:shadow-sky-100",
  },
  pink: {
    priceText: "text-pink-600",
    cta: "bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white",
    check: "text-pink-500",
    border: "border-pink-200",
    shadow: "shadow-lg shadow-pink-100/60 hover:shadow-pink-200/70",
  },
};

const PlanCard = ({ plan }) => {
  const isRec = plan.recommended;
  const a = ACCENTS[plan.accent];
  const goContacto = () => {
    document.querySelector("#contacto")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      onClick={goContacto}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && goContacto()}
      className={`group relative flex cursor-pointer flex-col rounded-[2rem] border bg-white p-8 pb-7 transition-all duration-300 hover:-translate-y-1 ${a.border} ${a.shadow} ${isRec ? "scale-[1.02] md:scale-[1.03]" : ""}`}
    >
      {isRec && (
        <div className="absolute -top-3 left-1/2 flex -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded-full bg-gradient-to-r from-pink-500 to-rose-500 px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-white shadow-md shadow-pink-200">
          <Star className="h-3 w-3 fill-current" />
          Más elegido
        </div>
      )}

      <p className="text-xs font-bold uppercase tracking-[0.3em] text-slate-500">
        {plan.tier}
      </p>

      <h3 className="mt-2 text-4xl font-black text-slate-900 md:text-5xl">
        {plan.speed}
      </h3>

      <div className="mt-5 flex items-baseline gap-1">
        <span className={`text-4xl font-black ${a.priceText} md:text-5xl`}>
          {plan.price}
        </span>
        <span className="text-base font-semibold text-slate-500">
          {plan.priceSuffix}
        </span>
      </div>

      <p className="mt-4 text-sm text-slate-600">{plan.description}</p>

      <div className="my-6 h-px w-full bg-slate-100" />

      <ul className="flex-1 space-y-3 text-sm text-slate-700">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-3">
            <span className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${plan.accent === "pink" ? "bg-pink-100" : "bg-sky-100"}`}>
              <Check className={`h-3 w-3 ${a.check}`} />
            </span>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <Button
        data-testid={`plan-cta-${plan.id}`}
        onClick={(e) => {
          e.stopPropagation();
          goContacto();
        }}
        className={`mt-7 w-full rounded-full py-6 text-base font-bold ${a.cta}`}
      >
        {plan.cta} →
      </Button>
    </div>
  );
};

const Planes = () => (
  <section id="planes" className="relative bg-gradient-to-b from-white via-pink-50/40 to-white py-14">
    <div className="mx-auto max-w-6xl px-6">
      <div className="mb-10 text-center">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-sky-500">
          Nuestros planes
        </p>
        <h2 className="mt-2 text-3xl font-black text-slate-900 md:text-4xl">
          Elegí tu velocidad
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-slate-600">
          Fibra óptica sin límite de datos, con WiFi y soporte local incluidos.
          Sin sorpresas, sin letra chica.
        </p>
      </div>

      <div className="grid items-stretch gap-8 md:grid-cols-2">
        {PLANS.map((plan) => (
          <PlanCard key={plan.id} plan={plan} />
        ))}
      </div>

      {/* Refuerzo comercial */}
      <div className="mx-auto mt-14 max-w-3xl text-center">
        <p className="text-lg font-bold leading-snug text-slate-800 md:text-xl">
          Con Silicomlan no llamás a un 0800 perdido. Tenés fibra óptica y{" "}
          <span className="bg-gradient-to-r from-pink-500 to-sky-500 bg-clip-text text-transparent">
            soporte local de verdad
          </span>
          .
        </p>
        <p className="mt-3 inline-flex items-center gap-2 text-sm text-slate-500">
          <Headphones className="h-4 w-4 text-sky-500" />
          Fibra óptica para Merlo, Zona Oeste y Baradero.
        </p>
      </div>

      {/* Nota TV opcional */}
      <div className="mx-auto mt-10 flex max-w-3xl items-start gap-3 rounded-2xl border border-sky-100 bg-sky-50/60 p-4 text-sm text-slate-600">
        <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-500" />
        <p>
          <b className="text-slate-800">¿Querés sumar TV?</b> Podés contratar
          por separado servicios externos como Netflix,
          Disney+ o Pack Fútbol. No forman parte del servicio de Silicomlan,
          pero funcionan perfecto sobre nuestra fibra óptica.
        </p>
      </div>
    </div>
  </section>
);

export default Planes;
